import { useEffect, useRef, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Activity, Clock, Play, Square, Loader2, ShieldCheck, Link2, ArrowRight, Cpu, TrendingUp, AlertTriangle,
  Radio, Video, DollarSign, Send, MessageCircle, CalendarClock,
} from "lucide-react";

const COLORS = { green: "#22e07b", yellow: "#f5c518", red: "#ff5b5b" };

function fmtCountdown(secs) {
  if (secs === null || secs === undefined) return "--:--:--";
  const s = Math.max(0, Math.floor(secs));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  if (d > 0) return `${d}d ${pad(h)}:${pad(m)}:${pad(sec)}`;
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

export default function SmartCpi() {
  const { account, bots, refreshAll } = useOutletContext();
  const navigate = useNavigate();
  const bot = bots?.[0];
  const [cpi, setCpi] = useState(null);
  const [showHow, setShowHow] = useState(false);
  const [left, setLeft] = useState(null);
  const [busy, setBusy] = useState(false);
  const tick = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/cpi/status");
      setCpi(data);
      setLeft(data.countdown_seconds);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const poll = setInterval(load, 30000);
    return () => clearInterval(poll);
  }, []);

  useEffect(() => {
    if (tick.current) clearInterval(tick.current);
    tick.current = setInterval(() => setLeft((v) => (v === null || v <= 0 ? v : v - 1)), 1000);
    return () => clearInterval(tick.current);
  }, [cpi?.release_time_utc]);

  const running = bot?.cpi_running === true;
  const mt5Connected = !!(account && account.metaapi_account_id && (account.metaapi_status === "linked" || account.status === "connected"));
  const isPropFirm = account?.type === "propfirm";
  const color = COLORS[cpi?.color] || COLORS.green;

  const toggle = async () => {
    if (!bot) { toast.error("Add Smart Scalper Fx first from the Home dashboard."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/bots/${bot.id}/cpi-toggle`);
      toast.success(data.cpi_running
        ? "Smart CPI Mode started — Smart Scalper is executing trades on MetaTrader."
        : "Smart CPI Mode stopped — managing open trades.");
      await refreshAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-5 pt-6 pb-10 fade-up space-y-5" data-testid="smart-cpi-page">
      <div>
        <div className="flex items-center gap-2">
          <Activity className="h-6 w-6" style={{ color }} />
          <h1 className="font-heading text-3xl font-bold tracking-tight">Smart CPI Mode</h1>
        </div>
        <p className="text-[#5f7ba6] text-sm mt-1">Institutional CPI / NFP / FOMC event trading — powered by Smart Scalper Fx.</p>
      </div>

      {/* Connection + bot status */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="cpi-status-row">
        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold ${mt5Connected ? "border-[#22e07b]/40 bg-[#22e07b]/10 text-[#22e07b]" : "border-[#ff5b5b]/40 bg-[#ff5b5b]/10 text-[#ff5b5b]"}`} data-testid="cpi-mt5-pill">
          <span className={`h-2 w-2 rounded-full ${mt5Connected ? "bg-[#22e07b]" : "bg-[#ff5b5b]"}`} />
          {mt5Connected ? "MT5 Connected" : "MT5 Disconnected"}
        </span>
        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold ${running ? "border-[#22e07b]/40 bg-[#22e07b]/10 text-[#22e07b]" : "border-[#ff5b5b]/40 bg-[#ff5b5b]/10 text-[#ff5b5b]"}`}>
          <span className={`h-2 w-2 rounded-full ${running ? "bg-[#22e07b] live-dot" : "bg-[#ff5b5b]"}`} />
          {running ? "CPI Mode Running" : "CPI Mode Stopped"}
        </span>
      </div>

      {/* Countdown hero */}
      <div className="rounded-3xl border border-[#14233d] bg-gradient-to-b from-[#0B1526] to-[#0A1120] p-6 text-center">
        <div className="flex items-center justify-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color, boxShadow: `0 0 10px ${color}` }} />
          <span className="font-heading font-semibold" data-testid="cpi-status-label">{cpi?.label || "Loading…"}</span>
        </div>
        <div className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6] mt-4 flex items-center justify-center gap-1.5">
          <Clock className="h-3 w-3" /> {cpi?.event_title || "USD CPI Release"}
        </div>
        <div data-testid="cpi-countdown" className="font-heading text-5xl font-bold tracking-tight mt-2 tabular-nums" style={{ color }}>
          {cpi?.status === "disabled" ? "N/A" : fmtCountdown(left)}
        </div>
        <div className="text-xs text-[#8fb4e6] mt-3">{cpi?.scheduled_et || ""}</div>
        <div className="flex flex-wrap justify-center gap-2 mt-4" data-testid="cpi-markets">
          {(cpi?.markets || []).map((m) => (
            <span key={m} className="px-3 py-1 rounded-full border border-[#1C8CFF]/40 bg-[#0e1a2e] text-xs font-semibold text-[#8fb4e6]">{m}</span>
          ))}
        </div>
      </div>

      {/* START / STOP */}
      {!mt5Connected ? (
        <Button onClick={() => navigate("/app/account/connect")} data-testid="cpi-connect-mt5-button"
          className="w-full h-14 rounded-full text-white font-semibold hover:opacity-90" style={{ backgroundColor: "var(--sfx-accent)" }}>
          <Link2 className="mr-2 h-5 w-5" /> Connect MT5 Account
        </Button>
      ) : isPropFirm ? (
        <div className="rounded-2xl border border-[#f5a623]/40 bg-[#f5a623]/10 p-5 flex items-start gap-3" data-testid="cpi-propfirm-block">
          <AlertTriangle className="h-5 w-5 text-[#f5a623] shrink-0 mt-0.5" />
          <div>
            <div className="font-heading font-semibold text-[#f5c07a]">CPI Mode is disabled on Prop Firm accounts</div>
            <p className="text-xs text-[#e8d5b5] mt-1 leading-relaxed">
              Your active account is a <b>Prop Firm ({account?.firm_name || "FTMO"})</b> account. Most prop firms
              (including FTMO) <b>ban news trading</b> around CPI/NFP/FOMC — running this would breach your challenge
              rules. Switch to a regular MT5 broker account in <b>MetaTrader</b> to use Smart CPI Mode.
            </p>
          </div>
        </div>
      ) : (
        <button onClick={toggle} disabled={busy} data-testid="cpi-start-stop-button"
          className="w-full h-16 rounded-full flex items-center justify-center gap-2 text-white font-bold text-lg transition-transform duration-200 hover:scale-[1.02] active:scale-95"
          style={{ backgroundColor: running ? "#ff3b30" : "var(--sfx-accent)", boxShadow: `0 0 28px ${running ? "rgba(255,59,48,0.4)" : "var(--sfx-accent-glow)"}` }}>
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : (
            <>{running ? <Square className="h-5 w-5" fill="white" /> : <Play className="h-5 w-5" fill="white" />}
              {running ? "STOP CPI MODE" : "START CPI MODE"}</>
          )}
        </button>
      )}

      {/* Live Analyst Room — watch a real analyst live before CPI/NFP/FOMC */}
      <LiveRoom />

      {/* How it works (collapsible to save space) */}
      <div className="rounded-2xl border border-[#14233d] bg-[#0A1120]">
        <button onClick={() => setShowHow((v) => !v)} data-testid="cpi-how-toggle"
          className="w-full flex items-center justify-between p-4 text-sm font-semibold">
          <span className="flex items-center gap-2"><Cpu className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} /> How Smart CPI Mode executes</span>
          <ArrowRight className={`h-4 w-4 text-[#5f7ba6] transition-transform ${showHow ? "rotate-90" : ""}`} />
        </button>
        {showHow && (
          <div className="px-4 pb-4 space-y-2.5">
            <p className="text-xs text-[#8fb4e6] leading-relaxed">
              Press START to let Smart Scalper trade for you on MetaTrader. The engine uses the <b className="text-white">Smart Scalper strategy (RSI + Bollinger + EMA)</b> and <b className="text-white">TradingView</b> confirmations, and executes real orders through <b className="text-white">MetaAPI</b> on your connected MT5 account — only when a high-probability setup is confirmed.
            </p>
            <ul className="text-xs text-[#8fb4e6] space-y-1.5 leading-relaxed list-disc pl-4">
              <li>Analyzes previous / forecast / actual CPI, USD strength, volatility, trend, liquidity &amp; spread.</li>
              <li>Never enters just because CPI released — full risk &amp; structure checks first.</li>
              <li>CPI Safety: monitors spread expansion &amp; avoids unsafe volatility spikes.</li>
            </ul>
          </div>
        )}
      </div>

      {/* Manage pairs shortcut */}
      <button onClick={() => navigate("/app/pairs", { state: { from: "cpi" } })} data-testid="cpi-pairs-link"
        className="w-full text-left border border-[#14233d] bg-[#0A1120] rounded-2xl p-4 flex items-center justify-between hover:border-[#1C8CFF]/50 transition-colors">
        <div className="flex items-center gap-2 text-sm"><ShieldCheck className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} /> Configure pairs, risk &amp; News Filter</div>
        <ArrowRight className="h-4 w-4 text-[#5f7ba6]" />
      </button>
    </div>
  );
}

/* ------------------------------- Live Analyst Room ------------------------------- */
function LiveRoom() {
  const [live, setLive] = useState(null);
  const [cd, setCd] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [chat, setChat] = useState([]);
  const [chatText, setChatText] = useState("");
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    const load = () => api.get("/live/session").then(({ data }) => setLive(data)).catch(() => {});
    load();
    const p = setInterval(load, 20000);
    return () => clearInterval(p);
  }, []);

  useEffect(() => {
    api.get("/live/schedule").then(({ data }) => setSchedule(data || [])).catch(() => {});
  }, [live?.is_live]);

  const isLive = !!live?.is_live;

  // Poll live chat while the session is live.
  useEffect(() => {
    if (!isLive) return;
    const load = () => api.get("/live/chat").then(({ data }) => setChat(data || [])).catch(() => {});
    load();
    const p = setInterval(load, 5000);
    return () => clearInterval(p);
  }, [isLive]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chat.length]);

  const sendChat = async () => {
    const m = chatText.trim();
    if (!m || sending) return;
    setChatText("");
    setSending(true);
    try {
      await api.post("/live/chat", { message: m });
      const { data } = await api.get("/live/chat");
      setChat(data || []);
    } catch { /* ignore */ }
    finally { setSending(false); }
  };

  useEffect(() => {
    if (!live?.event_time) { setCd(null); return; }
    const upd = () => setCd(Math.floor((new Date(live.event_time).getTime() - Date.now()) / 1000));
    upd();
    const t = setInterval(upd, 1000);
    return () => clearInterval(t);
  }, [live?.event_time]);

  return (
    <div className="rounded-2xl border border-[#14233d] bg-[#0A1120] overflow-hidden" data-testid="live-room">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#14233d]">
        <div className="flex items-center gap-2">
          {isLive ? (
            <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-[#ff2d2d]/15 text-[#ff5b5b] text-[11px] font-bold font-mono-term uppercase tracking-widest">
              <span className="h-2 w-2 rounded-full bg-[#ff2d2d] live-dot" /> LIVE
            </span>
          ) : (
            <Radio className="h-4 w-4 text-[#5f7ba6]" />
          )}
          <span className="text-sm font-semibold">Sal Analis an Dirèk</span>
        </div>
        {live?.event_type && (
          <span className="px-2 py-0.5 rounded-md text-[10px] font-bold font-mono-term" style={{ backgroundColor: "var(--sfx-accent-soft)", color: "var(--sfx-accent)" }}>{live.event_type}</span>
        )}
      </div>

      {/* Video */}
      <div className="relative bg-black aspect-video w-full" data-testid="live-video">
        {isLive && live?.embed_url ? (
          <iframe title="Live Analyst" src={live.embed_url} allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen className="absolute inset-0 w-full h-full" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 gap-2">
            <Video className="h-8 w-8 text-[#3f567d]" />
            <p className="text-sm text-[#8fb4e6] font-semibold">Pa gen analis an dirèk kounye a</p>
            <p className="text-xs text-[#5f7ba6] max-w-xs">
              {live?.event_title ? `Pwochen: ${live.event_title}` : "N ap fè yon live anvan pwochen gwo evènman (CPI / NFP / FOMC)."}
            </p>
          </div>
        )}
      </div>

      <div className="p-5 space-y-4">
        {live?.analyst_name && (
          <div className="text-xs text-[#8fb4e6]">Analis: <b className="text-white">{live.analyst_name}</b></div>
        )}

        {/* Countdown */}
        {live?.event_time && (
          <div className="rounded-xl border border-[#14233d] bg-[#060c18] px-4 py-3 flex items-center justify-between" data-testid="live-countdown">
            <span className="flex items-center gap-2 text-xs text-[#8fb4e6]"><Clock className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} /> {cd !== null && cd <= 0 ? "Evènman an lage" : "Evènman an nan"}</span>
            <span className="font-mono-term text-lg font-bold" style={{ color: cd !== null && cd <= 0 ? "#ff5b5b" : "var(--sfx-accent)" }}>{cd !== null && cd <= 0 ? "LIVE DATA" : fmtCountdown(cd)}</span>
          </div>
        )}

        {/* Event data */}
        {(live?.previous || live?.forecast || live?.actual) && (
          <div className="grid grid-cols-3 gap-2" data-testid="live-event-data">
            <DataCell label="Previous" value={live.previous} />
            <DataCell label="Forecast" value={live.forecast} />
            <DataCell label="Actual" value={live.actual} highlight />
          </div>
        )}

        {/* Analyst notes */}
        {(live?.usd_note || live?.gold_note) && (
          <div className="space-y-2">
            {live?.usd_note && (
              <div className="flex gap-2 text-xs text-[#a9c4ec]"><DollarSign className="h-4 w-4 shrink-0 text-[#22e07b]" /><span><b className="text-white">USD:</b> {live.usd_note}</span></div>
            )}
            {live?.gold_note && (
              <div className="flex gap-2 text-xs text-[#a9c4ec]"><TrendingUp className="h-4 w-4 shrink-0 text-[#f5c518]" /><span><b className="text-white">Gold (XAUUSD):</b> {live.gold_note}</span></div>
            )}
          </div>
        )}

        {/* Live Q&A chat — clients ask the analyst questions during the live */}
        {isLive && (
          <div className="pt-1" data-testid="live-chat">
            <div className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6] mb-2 flex items-center gap-1.5"><MessageCircle className="h-3.5 w-3.5" /> Poze kesyon bay analis la</div>
            <div className="border border-[#14233d] bg-[#060c18] rounded-xl h-52 overflow-y-auto p-3 space-y-2.5">
              {chat.length === 0 ? (
                <div className="text-xs text-[#5f7ba6] text-center py-8">Poze premye kesyon an…</div>
              ) : chat.map((m) => (
                <div key={m.id} className="text-xs" data-testid="live-chat-msg">
                  <span className={`font-semibold ${m.is_analyst ? "text-[#22e07b]" : "text-[#8fb4e6]"}`}>{m.name}{m.is_analyst ? " · Analyst" : ""}:</span>{" "}
                  <span className="text-[#cfe0f7]">{m.message}</span>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            <div className="flex gap-2 mt-2">
              <Input value={chatText} onChange={(e) => setChatText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendChat()} data-testid="live-chat-input"
                placeholder="Ekri yon kesyon..." className="h-11 rounded-xl bg-[#060c18] border-[#14233d] text-sm" />
              <Button onClick={sendChat} disabled={sending} data-testid="live-chat-send"
                className="h-11 w-11 p-0 rounded-xl shrink-0" style={{ backgroundColor: "var(--sfx-accent)" }}>
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        )}

        {/* Upcoming scheduled lives (Kalandriye Live) — shown when not live */}
        {!isLive && schedule.length > 0 && (
          <div data-testid="live-schedule">
            <div className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6] mb-2 flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> Pwochen Live yo</div>
            <div className="space-y-2">
              {schedule.map((s) => (
                <div key={s.id} className="flex items-center gap-2 border border-[#14233d] bg-[#060c18] rounded-xl px-3 py-2">
                  <span className="px-2 py-0.5 rounded-md text-[10px] font-bold font-mono-term" style={{ backgroundColor: "var(--sfx-accent-soft)", color: "var(--sfx-accent)" }}>{s.event_type}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold truncate">{s.title}</div>
                    <div className="font-mono-term text-[9px] text-[#5f7ba6]">{new Date(s.event_time).toLocaleString()}</div>
                  </div>
                  <Radio className="h-3 w-3 text-[#ff5b5b]" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DataCell({ label, value, highlight }) {
  return (
    <div className={`rounded-xl border p-3 text-center ${highlight ? "border-[var(--sfx-accent)]/40 bg-[var(--sfx-accent-soft)]" : "border-[#14233d] bg-[#060c18]"}`}>
      <div className="font-mono-term text-[9px] uppercase tracking-widest text-[#5f7ba6] mb-1">{label}</div>
      <div className={`font-heading text-base font-bold ${highlight ? "" : "text-white"}`} style={highlight ? { color: "var(--sfx-accent)" } : {}}>{value || "—"}</div>
    </div>
  );
}
